import { useState, useEffect, useCallback, type ReactNode } from 'react'
import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import {
  LuRefreshCw as RefreshCw,
  LuDatabase as Database,
} from 'react-icons/lu'
import { brand, themeColors } from '../../theme'
import { syncService } from '../../api/services'
import type { SyncStatus } from '../../api/services/sync.service'
import { T, useT } from '../../i18n'

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

interface SyncPageLayoutProps {
  title: string
  subtitle: string
  syncType: string
  onRefresh: () => void
  children: ReactNode
}

export default function SyncPageLayout({
  title,
  subtitle,
  syncType,
  onRefresh,
  children,
}: SyncPageLayoutProps) {
  const colors = themeColors
  const { t } = useT()
  const [status, setStatus] = useState<SyncStatus | undefined>()

  const fetchStatus = useCallback(() => {
    syncService.getStatus().then(statuses => {
      setStatus(statuses.find(s => s.syncType === syncType))
    }).catch(() => {})
  }, [syncType])

  useEffect(() => { fetchStatus() }, [fetchStatus])

  const inProgress = status?.lastPageProcessed != null && status.lastPageProcessed > 0

  return (
    <VStack gap={5} alignItems="stretch">
      {/* Header */}
      <Flex justifyContent="space-between" alignItems="center">
        <Box>
          <T fontSize="2xl" fontWeight="800" letterSpacing="-0.02em" color={colors.text}>{title}</T>
          <T fontSize="sm" color={colors.textMuted}>{subtitle}</T>
        </Box>
        <Box
          as="button" display="flex" alignItems="center" gap={2}
          px={4} py={2} bg="transparent" border="1px solid" borderColor={colors.border}
          color={colors.text} borderRadius="sm" fontWeight="bold" fontSize="sm"
          cursor="pointer" _hover={{ bg: colors.bgHover }} transition="all 0.2s"
          onClick={() => { fetchStatus(); onRefresh() }}
        >
          <RefreshCw size={16} />
          {t('sync.refresh')}
        </Box>
      </Flex>

      {/* Status Card */}
      {status && (
        <Box
          p={4} borderRadius="xl" border="1px solid" borderColor={colors.border}
          bg={colors.bgCard} maxW="400px"
        >
          <Flex alignItems="center" gap={2} mb={2}>
            <Database size={16} color={brand.green} />
            <Text fontSize="sm" fontWeight="600" color={brand.green}>{status.syncType}</Text>
          </Flex>
          <VStack gap={1} alignItems="stretch">
            <Flex justifyContent="space-between">
              <T tid="sync.records" fontSize="xs" color={colors.textMuted} />
              <Text fontSize="xs" fontWeight="600" color={colors.text}>
                {status.totalRecordsSynced?.toLocaleString() ?? '0'}
              </Text>
            </Flex>
            <Flex justifyContent="space-between">
              <T tid="sync.lastSync" fontSize="xs" color={colors.textMuted} />
              <Text fontSize="xs" fontWeight="500" color={colors.text}>
                {formatDate(status.lastSyncedAt)}
              </Text>
            </Flex>
            {status.lastPublicationDate && (
              <Flex justifyContent="space-between">
                <T tid="sync.publication" fontSize="xs" color={colors.textMuted} />
                <Text fontSize="xs" fontWeight="500" color={colors.text}>
                  {status.lastPublicationDate}
                </Text>
              </Flex>
            )}
            {inProgress && (
              <Box display="inline-block" px={2} py={0.5} borderRadius="md" fontSize="10px"
                fontWeight="bold" textTransform="uppercase" bg="rgba(212, 154, 42, 0.2)" color="#d49a2a" mt={1}
              >
                {t('sync.inProgress', { page: status.lastPageProcessed })}
              </Box>
            )}
          </VStack>
        </Box>
      )}

      {children}
    </VStack>
  )
}
